import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import type { GPUInstanceType } from "types/InstanceTypes";
import type { ContainerSelected } from "components/Environment/shared/BuildTypes";
import type {
  FirewallRule,
  NetworkConfiguration,
  NetworkService,
} from "./networkServices";
import agent, {
  type LaunchableParameter,
  type ModifyLaunchableRequest,
} from "server";
import type { LaunchableExposePorts } from "./types";
import { UserContext } from "contexts/UserContext";
import posthog from "posthog-js";
import { isNvidiaEmail } from "utils/freeTierUtils";
import {
  getPrimaryInstanceCloudCred,
  getPrimaryInstanceCloudCredId,
} from "utils/cloudCreds";
import { isStorageValueValid } from "utils/instanceTypes";
import type { AccessLevel } from "../shared/LaunchableAccessControl";
import { useHistory, useParams } from "react-router-dom";
import {
  getLaunchableNgcApiKeySpec,
  useLaunchable,
  validatePublicRemoteFile,
} from "../utils";
import { LaunchableContext } from "contexts/LaunchableContext";
import { toast } from "utils/toast";
import { LaunchableCreatorPageContainer } from "./layout";
import {
  getInitialNetworkConfiguration,
  getInitialNetworkServices,
  getInitialManagedServiceStatus,
  reconcileNetworkConfiguration,
} from "./managedNetworkServices";
import {
  createLaunchable,
  makeCreateLaunchableRequest,
  saveLaunchableEdits,
} from "./requests";
import { useSectionNavigation } from "./sectionNavigation";
import { DetailsSection } from "./sections/DetailsSection";
import { HardwareSection } from "./sections/HardwareSection";
import { LaunchableActionBar } from "./sections/LaunchableActionBar";
import { NetworkSection } from "./sections/NetworkSection";
import { ParametersSection } from "./sections/ParametersSection";
import { SourceSection } from "./sections/SourceSection";
import {
  getSoftwareConfigurationSummary,
  SoftwareSection,
} from "./sections/SoftwareSection";
import { ViewAccessSection } from "./sections/ViewAccessSection";
import type {
  BuilderSection,
  LaunchableCreatorProps,
  ManagedServiceStatus,
  RuntimeType,
  SectionStatus,
} from "./types";
import { hasLaunchableParameterErrors } from "../shared/LaunchableParameters";

export const hydrateContainerLifecycleScript = (
  container: ContainerSelected | undefined,
  lifecycleScript: string,
): ContainerSelected | undefined => {
  const scriptAttr = container?.vmBuild?.lifeCycleScriptAttr;
  const script = lifecycleScript || scriptAttr?.script;
  if (!container?.vmBuild || !scriptAttr || script === undefined) {
    return container;
  }

  return {
    ...container,
    vmBuild: {
      ...container.vmBuild,
      lifeCycleScriptAttr: {
        ...scriptAttr,
        script,
      },
    },
  };
};

export const getExposedPorts = (
  services: NetworkService[],
  firewallRules: FirewallRule[],
): LaunchableExposePorts => ({
  tunnels: services
    .filter((service) => !service.isFrontendOnly)
    .map((service) => ({
      id: service.id,
      name: service.name,
      port: Number(service.port),
      isCTA: service.isCTA,
      ctaLabel: service.ctaLabel,
      isEnvironmentManaged: service.isEnvironmentManaged,
    })),
  firewallRules: firewallRules.map((rule) => ({
    id: rule.id,
    port: rule.ports,
    allowedIPs: rule.allowedIPs,
  })),
});

export const LaunchableCreator: React.FC<LaunchableCreatorProps> = ({
  mode = "create",
  launchableId,
  initialData,
  rawLaunchableData,
  initialInstance,
  initialLifecycleScript = "",
}) => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const launchableContext = useContext(LaunchableContext);
  const history = useHistory();
  const isEditMode = mode === "edit";
  const {
    active: activeSection,
    departed: departedSections,
    advanced: advancedSections,
    openSection: handleSectionOpen,
  } = useSectionNavigation(isEditMode);
  const initialContainerWithoutLifecycleScript = initialData?.containerSelected
    ?.dockerCompose
    ? {
        ...initialData.containerSelected,
        dockerCompose: {
          ...initialData.containerSelected.dockerCompose,
          parsedYaml:
            initialData.containerSelected.dockerCompose.parsedYaml ||
            initialData.containerSelected.dockerCompose.yamlString,
        },
      }
    : initialData?.containerSelected;
  const initialContainer = hydrateContainerLifecycleScript(
    initialContainerWithoutLifecycleScript,
    initialLifecycleScript,
  );
  const initialRuntimeType: RuntimeType = initialContainer?.vmBuild
    ? initialContainer.vmBuild.k8s && !initialContainer.vmBuild.k8s.isDisabled
      ? "kubernetes"
      : "VM"
    : initialContainer
      ? "container"
      : "VM";
  const initialManagedServiceStatus = getInitialManagedServiceStatus({
    initialRuntimeType,
    initialContainer,
    isEditMode,
  });
  const allowFeaturedSingleContainer =
    isEditMode && Boolean(initialContainer?.verbBuild);
  const isNvidian = isNvidiaEmail(userContext.me?.email || "");
  const [imageId, setImageId] = useState<string>(initialData?.imageId || "");
  const [
    performDefaultBuildForCustomImage,
    setPerformDefaultBuildForCustomImage,
  ] = useState<boolean>(
    initialData?.performDefaultBuildForCustomImage ?? false,
  );
  const [runtimeType, setRuntimeType] =
    useState<RuntimeType>(initialRuntimeType);
  const [codeSource, setCodeSource] = useState<string>(initialData?.file || "");
  const [codeSourceType, setCodeSourceType] = useState<
    "url/upload" | "embedded" | "none" | ""
  >(initialData?.file ? "url/upload" : isEditMode ? "none" : "");
  const [codeSourceError, setCodeSourceError] = useState<string | null>(null);
  const [isValidatingCodeSource, setIsValidatingCodeSource] = useState(false);
  const codeSourceValidationVersion = useRef(0);
  const [selectedContainer, setSelectedContainer] = useState<
    ContainerSelected | undefined
  >(initialContainer);
  const [pythonVersion, setPythonVersion] = useState<string>(
    initialData?.python || "3.10",
  );
  const [cudaVersion, setCudaVersion] = useState<string>(
    initialData?.cuda || "12.0.1",
  );
  // lifecycle script
  const [lifecycleScript, setLifecycleScript] = useState<string>(
    initialLifecycleScript,
  );
  const [lifecycleScriptTab, setLifecycleScriptTab] = useState<
    "upload" | "paste"
  >("paste");
  const [lifecycleScriptFile, setLifecycleScriptFile] = useState<File | null>(
    null,
  );
  const [isLifecycleScriptValid, setIsLifecycleScriptValid] =
    useState<boolean>(true);
  const [description, setDescription] = useState<string>(
    initialData?.description || "",
  );
  const [viewAccess, setViewAccess] = useState<AccessLevel>(
    initialData?.viewAccess || "public",
  );
  const [parameters, setParameters] = useState<LaunchableParameter[]>(
    initialData?.parameters || [],
  );
  const [showParameterErrors, setShowParameterErrors] = useState(false);
  // Network and Jupyter configuration
  const [managedServiceStatus, setManagedServiceStatus] =
    useState<ManagedServiceStatus>(initialManagedServiceStatus);
  const [networkConfiguration, setNetworkConfiguration] =
    useState<NetworkConfiguration>(() =>
      getInitialNetworkConfiguration({
        savedConfiguration: {
          services: getInitialNetworkServices(
            rawLaunchableData?.buildRequest?.ports || [],
            initialContainer,
          ),
          firewallRules: (initialData?.firewallRules || []).map(
            (rule, index) => ({
              id: `existing-firewall-rule-${index}`,
              ports: rule.port,
              allowedIPs: rule.allowedIPs,
            }),
          ),
        },
        initialContainer: initialContainer || {
          vmBuild: {
            forceJupyterInstall: initialManagedServiceStatus === "configured",
          },
        },
        isEditMode,
      }),
    );
  const { services, firewallRules, configurationError } = networkConfiguration;
  const handleServicesChange = useCallback((services: NetworkService[]) => {
    setNetworkConfiguration((current) => ({ ...current, services }));
  }, []);
  const handleFirewallRulesChange = useCallback(
    (firewallRules: FirewallRule[]) => {
      setNetworkConfiguration((current) => ({ ...current, firewallRules }));
    },
    [],
  );

  //Compute
  const [selectedInstanceType, setSelectedInstanceType] = useState<
    GPUInstanceType | undefined
  >(initialInstance);
  const [storage, setStorage] = useState<string>(
    initialData?.diskStorage || "256",
  );
  const [location, setLocation] = useState<string>(initialData?.location || "");
  const [subLocation, setSubLocation] = useState<string>(
    initialData?.subLocation || "",
  );

  //Review
  const [name, setName] = useState<string>(initialData?.name || "");
  const [errorCreatingLaunchable, setErrorCreatingLaunchable] =
    useState<string>("");
  const [loadingCreatingLaunchable, setLoadingCreatingLaunchable] =
    useState<boolean>(false);

  const handleCodeSourceTypeChange = (
    type: "url/upload" | "embedded" | "none",
  ) => {
    codeSourceValidationVersion.current += 1;
    setIsValidatingCodeSource(false);
    setCodeSourceType(type);
    setCodeSourceError(null);
    if (type !== "url/upload") {
      setCodeSource("");
    }
  };

  const handleInstanceTypeChange = (
    instanceType: GPUInstanceType | undefined,
  ) => {
    setSelectedInstanceType(instanceType);
    setLocation("");
    setSubLocation("");
  };

  const handleCodeSourceChange = (source: string) => {
    codeSourceValidationVersion.current += 1;
    setIsValidatingCodeSource(false);
    setCodeSource(source);
    setCodeSourceError(null);
  };

  const validateCodeSource = useCallback(async (): Promise<boolean> => {
    if (codeSourceType !== "url/upload") return true;

    const candidateCodeSource = codeSource.trim();
    if (!candidateCodeSource) {
      setCodeSourceError("Enter a public repository or file URL");
      return false;
    }

    const validationVersion = ++codeSourceValidationVersion.current;
    setIsValidatingCodeSource(true);
    const validationError = await validatePublicRemoteFile(candidateCodeSource);
    if (validationVersion !== codeSourceValidationVersion.current) return false;

    setIsValidatingCodeSource(false);
    setCodeSourceError(validationError);
    return !validationError;
  }, [codeSource, codeSourceType]);

  const handleSoftwareContainerChange = (container: ContainerSelected) => {
    setSelectedContainer(container);

    if (container.vmBuild?.k8s && !container.vmBuild.k8s.isDisabled) {
      setRuntimeType("kubernetes");
      setManagedServiceStatus(
        container.vmBuild.k8s.isDashboardEnabled
          ? "configured"
          : "not-configured",
      );
      setLifecycleScript(container.vmBuild.lifeCycleScriptAttr?.script || "");
    } else if (container.vmBuild) {
      setRuntimeType("VM");
      setManagedServiceStatus(
        container.vmBuild.forceJupyterInstall ? "configured" : "not-configured",
      );
      setLifecycleScript(container.vmBuild.lifeCycleScriptAttr?.script || "");
      if (codeSourceType === "embedded") {
        setCodeSourceType("none");
      }
    } else if (container.dockerCompose) {
      setRuntimeType("container");
      setManagedServiceStatus(
        container.dockerCompose.jupyterInstall
          ? "configured"
          : "not-configured",
      );
    } else {
      setRuntimeType("container");
      setManagedServiceStatus("not-configured");
    }

    setNetworkConfiguration((current) =>
      reconcileNetworkConfiguration(current, container),
    );
  };

  const isNetworkNextDisabled = () => {
    if (
      services.some((service) => service.hasInvalidPortError) ||
      services.some((service) => service.hasPortRangeError) ||
      services.some((service) => service.hasPortConflict) ||
      services.some((service) => service.hasNameError) ||
      services.some((service) => service.hasNameConflict)
    ) {
      return true;
    }

    if (
      firewallRules.some((rule) => rule.hasInvalidPortError) ||
      firewallRules.some((rule) => rule.hasInvalidFormatError) ||
      firewallRules.some((rule) => rule.hasPortConflict)
    ) {
      return true;
    }
    const nonJupyterServices = services.filter((service) => !service.isJupyter);
    if (
      nonJupyterServices.length >= 1 &&
      nonJupyterServices.some(
        (service) => service.name === "" || service.port === "",
      )
    ) {
      return true;
    }

    return false;
  };

  const handleNameChange = (name: string) => {
    setName(name);
  };

  const handleParametersChange = (nextParameters: LaunchableParameter[]) => {
    setParameters(nextParameters);
    if (!hasLaunchableParameterErrors(nextParameters)) {
      setShowParameterErrors(false);
    }
  };

  const resolvedContainer = useMemo<ContainerSelected | undefined>(() => {
    if (runtimeType === "VM") {
      return {
        vmBuild: {
          forceJupyterInstall: managedServiceStatus === "configured",
          lifeCycleScriptAttr: lifecycleScript
            ? {
                ...selectedContainer?.vmBuild?.lifeCycleScriptAttr,
                script: lifecycleScript,
              }
            : undefined,
        },
      };
    }

    if (runtimeType === "kubernetes") {
      return {
        vmBuild: {
          forceJupyterInstall: false,
          k8s: {
            isDisabled: false,
            isDashboardEnabled: managedServiceStatus === "configured",
          },
          lifeCycleScriptAttr: lifecycleScript
            ? {
                ...selectedContainer?.vmBuild?.lifeCycleScriptAttr,
                script: lifecycleScript,
              }
            : undefined,
        },
      };
    }

    return selectedContainer;
  }, [lifecycleScript, managedServiceStatus, runtimeType, selectedContainer]);

  const softwareContainerForSelector = useMemo<ContainerSelected>(() => {
    if (selectedContainer) return selectedContainer;

    if (runtimeType === "kubernetes") {
      return {
        vmBuild: {
          forceJupyterInstall: false,
          k8s: {
            isDisabled: false,
            isDashboardEnabled: managedServiceStatus === "configured",
          },
        },
      };
    }

    return {
      vmBuild: {
        forceJupyterInstall: managedServiceStatus === "configured",
      },
    };
  }, [managedServiceStatus, runtimeType, selectedContainer]);

  const detailsComplete = !!name.trim();
  const hardwareComplete =
    !!selectedInstanceType && isStorageValueValid(storage, selectedInstanceType);

  const sourceComplete =
    !codeSourceType ||
    codeSourceType === "embedded" ||
    codeSourceType === "none" ||
    (codeSourceType === "url/upload" &&
      !!codeSource.trim() &&
      !codeSourceError &&
      !isValidatingCodeSource);

  const softwareComplete =
    isLifecycleScriptValid &&
    (() => {
      if (runtimeType === "VM" || runtimeType === "kubernetes") {
        return true;
      }

      if (runtimeType !== "container") {
        return false;
      }

      if (selectedContainer?.customContainer) {
        return !!selectedContainer.customContainer.containerUrl.trim();
      }

      if (selectedContainer?.dockerCompose) {
        return !!(
          selectedContainer.dockerCompose.yamlString ||
          selectedContainer.dockerCompose.fileUrl
        );
      }

      if (selectedContainer?.verbBuild) {
        return !!selectedContainer.verbBuild.containerUrl;
      }

      return false;
    })();

  const networkHasError =
    Boolean(configurationError) || isNetworkNextDisabled();
  const networkComplete = !networkHasError;
  const parametersHaveError = hasLaunchableParameterErrors(parameters);
  const parametersComplete = !parametersHaveError;
  const canCreate =
    detailsComplete &&
    sourceComplete &&
    softwareComplete &&
    networkComplete &&
    parametersComplete &&
    hardwareComplete &&
    !!resolvedContainer;

  const getSectionStatus = (
    section: BuilderSection,
    isComplete: boolean,
    isRequired = false,
  ): SectionStatus => {
    const hasVisited = activeSection === section || departedSections[section];

    if (isComplete && (isRequired || hasVisited)) return "complete";
    if (isComplete) return "optional";
    if (departedSections[section]) return "incomplete";
    return isRequired ? "required" : "optional";
  };

  const getContainerType = (container?: ContainerSelected) => {
    if (container?.customContainer) return "Custom Container";
    if (container?.dockerCompose) return "Docker Compose";
    if (container?.verbBuild) return "Verb Build";
    if (container?.vmBuild?.k8s && !container.vmBuild.k8s.isDisabled) {
      return "Kubernetes";
    }
    if (container?.vmBuild) return "VM Build";
    return "Container";
  };

  const handleSourceNext = async () => {
    if (await validateCodeSource()) {
      handleSectionOpen("network", true);
    }
  };

  const handleSaveLaunchable = async () => {
    setErrorCreatingLaunchable("");

    if (!selectedInstanceType || !resolvedContainer || !canCreate) {
      setErrorCreatingLaunchable(
        `Complete the required fields before ${isEditMode ? "saving" : "creating"}.`,
      );
      return;
    }

    if (!(await validateCodeSource())) {
      handleSectionOpen("source");
      return;
    }

    setLoadingCreatingLaunchable(true);
    try {
      const createLaunchableRequest = makeCreateLaunchableRequest(
        name.trim(),
        description.trim(),
        viewAccess,
        storage,
        selectedInstanceType,
        pythonVersion,
        cudaVersion,
        resolvedContainer,
        codeSource,
        getExposedPorts(services, firewallRules),
        {
          imageId: imageId || undefined,
          performDefaultBuildForCustomImage,
          location: location || undefined,
          subLocation: subLocation || undefined,
          parameters,
        },
      );
      if (isEditMode) {
        if (!launchableId) {
          throw new Error("Launchable ID is required to save changes.");
        }

        const modifyRequest: ModifyLaunchableRequest = {
          id: launchableId,
          name: createLaunchableRequest.name,
          description: createLaunchableRequest.description,
          createWorkspaceRequest:
            createLaunchableRequest.createWorkspaceRequest,
          buildRequest: createLaunchableRequest.buildRequest,
          file: createLaunchableRequest.file,
          ngcApiKeySpec: getLaunchableNgcApiKeySpec(rawLaunchableData),
        };
        const editResponse = await saveLaunchableEdits({
          orgId: orgContext.activeOrgId,
          request: modifyRequest,
          viewAccess,
          originalViewAccess: initialData?.viewAccess || "public",
        });

        if (!editResponse.success) {
          throw new Error(
            editResponse.message ||
              "Failed to update launchable. Please try again.",
          );
        }

        await launchableContext.refreshActiveLaunchables();
        toast.success("Launchable updated");
        history.push(`/org/${orgContext.activeOrgId}/launchables?tab=mine`);
        return;
      }

      const res = await createLaunchable(
        orgContext.activeOrgId,
        createLaunchableRequest,
      );
      if (!res)
        throw new Error("Failed to create launchable. Please try again.");

      const launchableDeployPath = `/launchable/deploy?launchableID=${encodeURIComponent(
        res.id,
      )}`;
      posthog.capture("Launchable Created", {
        launchableID: res.id,
        launchableInstanceType: selectedInstanceType.type,
        cloudCredID: getPrimaryInstanceCloudCred(selectedInstanceType)?.id,
        launchableCreatedByUserID: userContext.me?.id,
        launchableCreatedByOrgID: orgContext.activeOrgId,
        containerType: getContainerType(resolvedContainer),
        file: codeSource,
        includeFirewallRules: firewallRules.length > 0,
        includeServices: services.length > 0,
        includeUseExistingCompose: Boolean(
          resolvedContainer.dockerCompose?.fileUrl,
        ),
        name: name.trim(),
      });

      await launchableContext.refreshActiveLaunchables();
      toast.success(
        <>
          Launchable <strong>{name.trim()}</strong> created.
        </>,
        {
          duration: 10000,
          action: {
            label: (
              <span className="inline-flex items-center gap-1">
                View launchable
                <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            ),
            onClick: () =>
              window.open(
                launchableDeployPath,
                "_blank",
                "noopener,noreferrer",
              ),
          },
        },
      );
      history.push(`/org/${orgContext.activeOrgId}/launchables?tab=mine`);
      return;
    } catch (error) {
      setErrorCreatingLaunchable(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "save" : "create"} launchable.`,
      );
    } finally {
      setLoadingCreatingLaunchable(false);
    }
  };

  const sourceSummary = (() => {
    if (!codeSourceType) return "No code files";
    if (codeSourceType === "none") return "No code files";
    if (codeSourceType === "embedded") return "Code is embedded in the image";
    return codeSource || "Repository or file URL required";
  })();

  const softwareSummary = getSoftwareConfigurationSummary({
    runtimeType,
    container: selectedContainer,
    managedServiceStatus,
    lifecycleScript,
    imageId,
    services,
    pythonVersion,
    cudaVersion,
  });

  const networkSummary =
    services.length > 0 || firewallRules.length > 0
      ? undefined
      : "Default network access";

  return (
    <LaunchableCreatorPageContainer>
      <div className="mb-5">
        <div>
          <h1 className="mb-4 text-2xl font-semibold text-primary">
            {isEditMode ? "Edit Launchable" : "New Launchable"}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-secondary">
            Create an environment template that can be shared with your team or
            the larger Brev community.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <main className="space-y-4">
          <DetailsSection
            name={name}
            description={description}
            isEditMode={isEditMode}
            loadingCreatingLaunchable={loadingCreatingLaunchable}
            onNameChange={handleNameChange}
            onDescriptionChange={setDescription}
            status={getSectionStatus("details", detailsComplete, true)}
            showCompletionIcon={advancedSections.details && detailsComplete}
            canContinue={detailsComplete}
            onNext={() => handleSectionOpen("hardware", true)}
            activeSection={activeSection}
            hasBeenOpened={
              activeSection === "details" || departedSections.details
            }
            setActiveSection={handleSectionOpen}
          />

          <HardwareSection
            selectedInstanceType={selectedInstanceType}
            onInstanceTypeChange={handleInstanceTypeChange}
            storage={storage}
            onStorageChange={setStorage}
            location={location}
            onLocationChange={setLocation}
            subLocation={subLocation}
            onSubLocationChange={setSubLocation}
            status={getSectionStatus("hardware", hardwareComplete, true)}
            showCompletionIcon={advancedSections.hardware && hardwareComplete}
            canContinue={hardwareComplete}
            onNext={() => handleSectionOpen("software", true)}
            activeSection={activeSection}
            hasBeenOpened={
              activeSection === "hardware" || departedSections.hardware
            }
            setActiveSection={handleSectionOpen}
          />

          <SoftwareSection
            summary={softwareSummary}
            softwareContainerForSelector={softwareContainerForSelector}
            onContainerChange={handleSoftwareContainerChange}
            pythonVersion={pythonVersion}
            onPythonVersionChange={setPythonVersion}
            cudaVersion={cudaVersion}
            onCudaVersionChange={setCudaVersion}
            lifecycleScriptTab={lifecycleScriptTab}
            onLifecycleScriptTabChange={setLifecycleScriptTab}
            lifecycleScriptFile={lifecycleScriptFile}
            onLifecycleScriptFileChange={setLifecycleScriptFile}
            onLifecycleScriptValidationChange={(hasError) =>
              setIsLifecycleScriptValid(!hasError)
            }
            isNvidian={isNvidian}
            runtimeType={runtimeType}
            imageId={imageId}
            onImageIdChange={setImageId}
            performDefaultBuildForCustomImage={
              performDefaultBuildForCustomImage
            }
            onPerformDefaultBuildForCustomImageChange={
              setPerformDefaultBuildForCustomImage
            }
            allowFeaturedSingleContainer={allowFeaturedSingleContainer}
            status={getSectionStatus("software", softwareComplete)}
            showCompletionIcon={advancedSections.software && softwareComplete}
            canContinue={softwareComplete}
            onNext={() => handleSectionOpen("source", true)}
            activeSection={activeSection}
            hasBeenOpened={
              activeSection === "software" || departedSections.software
            }
            setActiveSection={handleSectionOpen}
          />

          <SourceSection
            summary={sourceSummary}
            codeSource={codeSource}
            onCodeSourceChange={handleCodeSourceChange}
            codeSourceType={codeSourceType}
            onCodeSourceTypeChange={handleCodeSourceTypeChange}
            codeSourceError={codeSourceError}
            onCodeSourceErrorChange={setCodeSourceError}
            runtimeType={runtimeType}
            status={getSectionStatus("source", sourceComplete)}
            showCompletionIcon={advancedSections.source && sourceComplete}
            canContinue={sourceComplete}
            onNext={handleSourceNext}
            activeSection={activeSection}
            hasBeenOpened={
              activeSection === "source" || departedSections.source
            }
            setActiveSection={handleSectionOpen}
          />

          <NetworkSection
            summary={networkSummary}
            hasError={networkHasError}
            services={services}
            onServicesChange={handleServicesChange}
            runtimeType={runtimeType}
            resolvedContainer={resolvedContainer}
            firewallRules={firewallRules}
            onFirewallRulesChange={handleFirewallRulesChange}
            configurationError={configurationError}
            status={getSectionStatus("network", networkComplete)}
            showCompletionIcon={advancedSections.network && networkComplete}
            canContinue={networkComplete}
            onNext={() => handleSectionOpen("parameters", true)}
            activeSection={activeSection}
            hasBeenOpened={
              activeSection === "network" || departedSections.network
            }
            setActiveSection={handleSectionOpen}
          />

          <ParametersSection
            parameters={parameters}
            hasError={parametersHaveError}
            showErrors={showParameterErrors}
            loadingCreatingLaunchable={loadingCreatingLaunchable}
            onParametersChange={handleParametersChange}
            status={getSectionStatus("parameters", parametersComplete)}
            showCompletionIcon={
              advancedSections.parameters && parametersComplete
            }
            canContinue
            onNext={() => {
              if (parametersHaveError) {
                setShowParameterErrors(true);
                return;
              }
              handleSectionOpen(null, true);
              requestAnimationFrame(() => {
                document
                  .getElementById(
                    isEditMode ? "edit-launchable" : "create-launchable",
                  )
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }}
            activeSection={activeSection}
            hasBeenOpened={
              activeSection === "parameters" || departedSections.parameters
            }
            setActiveSection={handleSectionOpen}
          />

          <ViewAccessSection
            isEditMode={isEditMode}
            viewAccess={viewAccess}
            onViewAccessChange={setViewAccess}
            loadingCreatingLaunchable={loadingCreatingLaunchable}
          />

          <LaunchableActionBar
            canCreate={canCreate}
            isEditMode={isEditMode}
            loadingCreatingLaunchable={loadingCreatingLaunchable}
            onSave={handleSaveLaunchable}
            onCancel={() => history.goBack()}
          />

          {errorCreatingLaunchable && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
              {errorCreatingLaunchable}
            </div>
          )}
        </main>
      </div>
    </LaunchableCreatorPageContainer>
  );
};

export const EditLaunchableCreatorPage: React.FC = () => {
  const { launchableId } = useParams<{ launchableId: string }>();
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const { launchableData, rawLaunchableData, loading, error } = useLaunchable(
    launchableId,
    false,
  );
  const [instanceRequest, setInstanceRequest] = useState<{
    key: string;
    data?: GPUInstanceType;
    error?: string;
  }>({ key: "" });
  const [scriptRequest, setScriptRequest] = useState<{
    key: string;
    data?: string;
    error?: string;
  }>({ key: "" });
  const availableInstances = useMemo(
    () => [
      ...instanceTypesContext.GPUInstancesAvailable,
      ...instanceTypesContext.CPUInstancesAvailable,
    ],
    [
      instanceTypesContext.CPUInstancesAvailable,
      instanceTypesContext.GPUInstancesAvailable,
    ],
  );
  const matchingInstance = availableInstances.find(
    (candidate) =>
      candidate.type === launchableData?.instanceType &&
      getPrimaryInstanceCloudCredId(candidate) === launchableData?.cloudCredId,
  );
  const instanceKey = `${launchableData?.instanceType || ""}:${launchableData?.cloudCredId || ""}`;
  const instance =
    matchingInstance ||
    (instanceRequest.key === instanceKey ? instanceRequest.data : undefined);
  const lifecycleScriptId =
    launchableData?.containerSelected?.vmBuild?.lifeCycleScriptAttr?.id;
  const lifecycleScript = lifecycleScriptId
    ? scriptRequest.key === lifecycleScriptId
      ? scriptRequest.data
      : undefined
    : "";
  const hydrationError =
    (instanceRequest.key === instanceKey ? instanceRequest.error : undefined) ||
    (lifecycleScriptId && scriptRequest.key === lifecycleScriptId
      ? scriptRequest.error
      : undefined);

  useEffect(() => {
    if (!launchableData?.instanceType || !launchableData.cloudCredId) return;

    if (matchingInstance) {
      return;
    }

    let cancelled = false;
    agent.Instances.getInstanceByType(
      launchableData.instanceType,
      launchableData.cloudCredId,
    ).then(
      (response) => {
        if (cancelled) return;
        if (response.success && response.data?.[0]) {
          setInstanceRequest({ key: instanceKey, data: response.data[0] });
        } else {
          setInstanceRequest({
            key: instanceKey,
            error:
              response.message || "The configured hardware is unavailable.",
          });
        }
      },
      () => {
        if (!cancelled) {
          setInstanceRequest({
            key: instanceKey,
            error: "Unable to load the configured hardware.",
          });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [
    instanceKey,
    launchableData?.cloudCredId,
    launchableData?.instanceType,
    matchingInstance,
  ]);

  useEffect(() => {
    if (!lifecycleScriptId) return;

    let cancelled = false;
    agent.Workspaces.getLifeCycleScript(launchableId, lifecycleScriptId).then(
      (response) => {
        if (!cancelled) {
          setScriptRequest({
            key: lifecycleScriptId,
            data: response.data?.attrs?.script || "",
          });
        }
      },
      () => {
        if (!cancelled) {
          setScriptRequest({
            key: lifecycleScriptId,
            error: "Unable to load the setup script.",
          });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [launchableId, lifecycleScriptId]);

  if (error && !loading) {
    return (
      <LaunchableCreatorPageContainer className="max-w-3xl text-sm text-red-400">
        {error}
      </LaunchableCreatorPageContainer>
    );
  }

  if (hydrationError) {
    return (
      <LaunchableCreatorPageContainer className="max-w-3xl text-sm text-red-400">
        {hydrationError}
      </LaunchableCreatorPageContainer>
    );
  }

  if (
    loading ||
    !launchableData ||
    !rawLaunchableData ||
    !instance ||
    lifecycleScript === undefined
  ) {
    return (
      <LaunchableCreatorPageContainer className="flex min-h-[50vh] items-center justify-center text-secondary">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading launchable...
      </LaunchableCreatorPageContainer>
    );
  }

  return (
    <LaunchableCreator
      key={launchableId}
      mode="edit"
      launchableId={launchableId}
      initialData={launchableData}
      rawLaunchableData={rawLaunchableData}
      initialInstance={instance}
      initialLifecycleScript={lifecycleScript}
    />
  );
};
